import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight, faCodeMerge } from "@fortawesome/free-solid-svg-icons"

import { AuditRecord } from "types/questionnaire.types"
import { HashChip } from "shared/ui/HashChip/HashChip.component"

// The wave-over-wave headline: what the merge actually had to reconcile. Reading the counts
// off the collapsed row is the point — the whole reason to record the delta was so nobody has
// to open a 775-row log to learn that 42 things changed.
export const structuralSummary = (rec: AuditRecord): string => {
  const s = rec.structural
  if (!s) return ""
  const bits: string[] = []
  if (s.onlyInAdd?.length) bits.push(`${s.onlyInAdd.length} new`)
  if (s.onlyInBase?.length) bits.push(`${s.onlyInBase.length} dropped`)
  if (s.retyped?.length) bits.push(`${s.retyped.length} retyped`)
  if (s.skipped?.length) bits.push(`${s.skipped.length} skipped`)
  if (s.renamed?.length) bits.push(`${s.renamed.length} renamed`)
  if (s.errors?.length) bits.push(`${s.errors.length} errored`)
  return bits.length ? bits.join(" · ") : "no structural differences"
}

// "New in Wave 6" reads; "New in add" does not. Falls back to the role when the merge
// carried no wave numbers.
const waveLabel = (rec: AuditRecord, role: "base" | "add", verb: string): string => {
  const p = rec.parents?.find((x) => x.role === role)
  return p?.wave != null ? `${verb} Wave ${p.wave}` : `${verb} the ${role} file`
}

const BUCKET_TONE: Record<string, string> = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  violet: "border-violet-200 bg-violet-50 text-violet-700",
  rose: "border-rose-200 bg-rose-50 text-rose-700",
  slate: "border-slate-200 bg-slate-50 text-slate-600",
}

/** One bucket of the wave-over-wave delta. Collapsed to a count until asked to expand —
 *  the count is the answer most of the time; the paths are for when it isn't. */
const StructuralBucket: React.FC<{
  label: string
  hint?: string
  paths?: string[]
  tone: keyof typeof BUCKET_TONE
}> = ({ label, hint, paths, tone }) => {
  const [open, setOpen] = useState(false)
  if (!paths?.length) return null
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 text-left"
      >
        <FontAwesomeIcon
          icon={faChevronRight}
          className={`h-2.5 w-2.5 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`}
        />
        <span
          className={`rounded-full border px-1.5 py-px text-[10px] font-medium ${BUCKET_TONE[tone]}`}
        >
          {label} · {paths.length}
        </span>
        {hint && <span className="truncate text-[10px] text-slate-400">{hint}</span>}
      </button>
      {open && (
        <ul className="mt-1 max-h-40 list-none overflow-y-auto pl-5 text-[11px] text-slate-600">
          {paths.map((p) => (
            <li key={p} className="truncate py-px font-mono" title={p}>
              {p}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// The one-line summary of a merge, for the collapsed row.
export const mergeSummary = (rec: AuditRecord): string => {
  const n = rec.parents?.length ?? 2
  const cases = rec.report?.merged_cases ?? rec.report?.output_respondents
  const struct = structuralSummary(rec)
  return `Built by merging ${n} projects${rec.operation ? ` (${rec.operation})` : ""}${
    cases != null ? ` — ${cases.toLocaleString()} cases` : ""
  }${struct ? ` · ${struct}` : ""}`
}

/** A data merge: the source projects, the wave-over-wave delta and the merged file. */
export const MergeRecordDetails: React.FC<{
  rec: AuditRecord
  onViewLineage?: (hash: string) => void
  onViewAncestry?: () => void
}> = ({ rec, onViewLineage, onViewAncestry }) => (
  <div className="rounded-md border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-[12px] text-slate-600">
    <div className="flex items-center gap-1.5 font-medium text-indigo-700">
      <FontAwesomeIcon icon={faCodeMerge} className="h-3 w-3" />
      Built by merging {rec.parents?.length ?? 2} projects
    </div>
    <div className="mt-1 text-slate-500">
      {rec.operation === "connect"
        ? `Joined horizontally on ${rec.idPath || "an ID variable"}${rec.join ? ` · keeping ${rec.join}` : ""}`
        : `Cases appended${rec.keep ? ` · keeping ${rec.keep} variables` : ""}`}
    </div>

    {rec.parents?.map((p) => (
      <div
        key={`${p.role}-${p.projectId}`}
        className="mt-2 rounded border border-indigo-100 bg-white/70 px-2 py-1.5"
      >
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-px text-[10px] font-medium text-indigo-700">
            {p.role}
          </span>
          <span className="font-mono text-[10px] text-slate-500">
            {p.projectId.slice(0, 8)}…
            {p.headRecordHash ? ` @ #${p.headRecordHash.slice(0, 10)}` : ""}
          </span>
          {p.wave != null && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-px text-[10px] font-medium text-slate-600">
              WAVE {p.wave}
            </span>
          )}
        </div>
        {p.derive?.query && (
          <div className="mt-1 text-[11px] text-slate-500">
            derived with <span className="font-medium">{p.derive.query}</span>
          </div>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {p.dataHash && <HashChip hash={p.dataHash} label="source" length={10} />}
          {p.derive?.scriptHash && <HashChip hash={p.derive.scriptHash} label="script" length={10} />}
          {p.projectVersion && <HashChip hash={p.projectVersion} label="version" length={10} />}
          {p.lineage?.hash && (
            <HashChip
              hash={p.lineage.hash}
              label={`lineage${p.lineage.nodeCount != null ? ` (${p.lineage.nodeCount})` : ""}`}
              length={10}
            />
          )}
        </div>
        {p.lineage?.hash && onViewLineage && (
          <button
            type="button"
            onClick={() => onViewLineage(p.lineage!.hash)}
            className="mt-1 text-[11px] text-slate-500 underline"
          >
            View captured lineage
          </button>
        )}
      </div>
    ))}

    {rec.structural && (
      <div className="mt-2 rounded border border-indigo-100 bg-white/70 px-2 py-1.5">
        <div className="font-medium text-slate-600">Differences between the waves</div>
        <StructuralBucket
          label={waveLabel(rec, "add", "New in")}
          hint="only in the later file — the earlier wave carries blanks"
          paths={rec.structural.onlyInAdd}
          tone="emerald"
        />
        <StructuralBucket
          label={waveLabel(rec, "base", "Dropped after")}
          hint="only in the earlier file — the later wave carries blanks"
          paths={rec.structural.onlyInBase}
          tone="amber"
        />
        <StructuralBucket
          label="Type changed"
          hint="reshaped so both waves could reconcile (e.g. single-select → multi-select)"
          paths={rec.structural.retyped}
          tone="violet"
        />
        <StructuralBucket
          label="Skipped"
          hint={`excluded by the ${rec.keep || rec.join || "keep"} mode`}
          paths={rec.structural.skipped}
          tone="slate"
        />
        <StructuralBucket
          label="Renamed"
          hint="renamed to avoid a collision"
          paths={rec.structural.renamed}
          tone="slate"
        />
        <StructuralBucket label="Errored" paths={rec.structural.errors} tone="rose" />
        {rec.structural.unchanged != null && (
          <div className="mt-1 text-[11px] text-slate-400">
            {rec.structural.unchanged.toLocaleString()} variables merged unchanged
          </div>
        )}
      </div>
    )}

    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {rec.dataHash && <HashChip hash={rec.dataHash} label="merged" length={10} />}
    </div>
    {onViewAncestry && (
      <button
        type="button"
        onClick={onViewAncestry}
        className="mt-1 text-[11px] text-slate-500 underline"
      >
        View ancestry
      </button>
    )}
  </div>
)
